import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { AuthService } from '@application/authentication/auth.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats } from '@application/time/date.service';
import {
	ActionCard,
	AutoFocus,
	BadgeText,
	BreakPoints,
	Button,
	CellulaFormSubmitData,
	DialogAlert,
	DialogModal,
	Flex,
	Form,
	IconButton,
	InputCombobox,
	Sheet,
	Text,
	useBreakpoint,
	useFormContext,
} from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AvatarInfo } from '@component/avatar_info.component';
import { DataList } from '@component/data_list.component';
import { Shimmer } from '@component/new_shimmer.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { WeeklyCalender } from '@component/weekly_calender.component';
import { Auth } from '@feature/auth/auth.component';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import { LocalityType } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { AnimatePresence, motion } from 'framer-motion';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';
import { TimeSlotInfo } from '../screen_conclusion';
import { ScreenTimeSlotProvider, useScreenTimeSlot } from './use_time_slot';

export function ScreenTimeSlot() {
	const viewBooking = useBooking();
	const routerLocation = useLocation();

	function handleGoBack(event: Event) {
		if (
			typeof routerLocation.state === 'object' &&
			routerLocation.state != null &&
			'bookingAutoForwardedFromLocation' in routerLocation.state &&
			routerLocation.state.bookingAutoForwardedFromLocation === true
		) {
			event.preventDefault();
			viewBooking.goToScreen(BookingScreen.ReasonForVisit, { replace: true });
		}
	}

	return (
		<ScreenTimeSlotProvider>
			<TopBar.Content goBack goBackIntercept={handleGoBack} />

			<Routes>
				<Route path='digital' element={<ScreenTimeSlotContent localityType={LocalityType.Digital} />} />
				<Route path=':locationId' element={<ScreenTimeSlotContent localityType={LocalityType.Physical} />} />
			</Routes>
		</ScreenTimeSlotProvider>
	);
}

function ScreenTimeSlotContent({ localityType }: ScreenTimeSlotContentProps) {
	const params = useParams();
	const screenTimeSlot = useScreenTimeSlot();
	const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
	const isSmallDevice = useBreakpoint(BreakPoints.Small);
	const analytic = useAnalyticContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const [timeSlotDetailsOpen, setTimeSlotDetailsOpen] = useState(false);
	const setupKeyRef = useRef<string | null>(null);
	const clearSelectTimeSlotRef = useRef(screenTimeSlot.clearSelectTimeSlot);

	useEffect(() => {
		clearSelectTimeSlotRef.current = screenTimeSlot.clearSelectTimeSlot;
	}, [screenTimeSlot.clearSelectTimeSlot]);

	useEffect(
		function whenTimeSlotDetailsDataChanges() {
			if (screenTimeSlot.timeSlotDetails.status === 'idle') {
				setTimeSlotDetailsOpen(false);
				return;
			}

			setTimeSlotDetailsOpen(true);
		},
		[screenTimeSlot.timeSlotDetails],
	);

	function handleTimeSlotDetailsOpenChange(open: boolean) {
		setTimeSlotDetailsOpen(open);

		if (!open) {
			analytic.trackEvent(ViewBookingAnalyticActions.CloseTimeSlotDetails);
			screenTimeSlot.clearTimeSlotDetails();
		}
	}

	function handlePreviewTimeSlotClick(timeSlotId: string) {
		return (event: MouseEvent<HTMLButtonElement>) => {
			analytic.trackEvent('Click time slot');
			triggerButtonRef.current = event.currentTarget as HTMLButtonElement;
			screenTimeSlot.previewTimeSlot(timeSlotId);
		};
	}

	function handleGoToNextAvailableDate() {
		if (screenTimeSlot.nextAvailableDate) {
			screenTimeSlot.setCurrentDate(screenTimeSlot.nextAvailableDate);
		}
	}

	function handleAutoFocusOnDetailClose(event: Event) {
		event.preventDefault();
		triggerButtonRef.current?.focus();
	}

	function handleFilterChange(formData: CellulaFormSubmitData) {
		screenTimeSlot.setFilter({
			resourceId: formData.resourceId ?? null,
			specialityId: formData.specialityId ?? null,
			unitId: formData.unitId ?? null,
		});
	}

	useEffect(
		function whenRouteParamsChange() {
			const setupKey = `${localityType}:${params.locationId ?? ''}`;
			if (setupKeyRef.current === setupKey) return;
			setupKeyRef.current = setupKey;

			async function fetchData() {
				await screenTimeSlot.clearTimeSlotDetails();
				if (setupKeyRef.current === setupKey) {
					screenTimeSlot.setup(localityType, params.locationId);
				}
			}
			fetchData();
		},
		[screenTimeSlot.clearTimeSlotDetails, screenTimeSlot.setup, localityType, params.locationId],
	);

	useEffect(function whenMount() {
		if (__visiba_feature_flags__['CareExperience.PW.Idp']) {
			// Nice, we don't need to do any quirks when using IDP 😌
		} else {
			function handleWhenOldSignInIsTriggered() {
				setTimeSlotDetailsOpen(false);
			}

			function handleWhenOldSignInIsAborted() {
				clearSelectTimeSlotRef.current();
				setTimeSlotDetailsOpen(true);
			}

			window.addEventListener('triggerSignIn', handleWhenOldSignInIsTriggered);
			window.addEventListener('abortSignIn', handleWhenOldSignInIsAborted);

			return () => {
				window.removeEventListener('triggerSignIn', handleWhenOldSignInIsTriggered);
				window.removeEventListener('abortSignIn', handleWhenOldSignInIsAborted);
			};
		}
	}, []);

	useEffect(function setFocusToTimeSlotListWhenChangingDate() {
		containerRef.current?.focus();
	}, []);

	const timeSlotsLoading = (
		<Flex gap={tokens.spacing.x1}>
			<Shimmer height={80} width={'100%'} />
			<Shimmer height={80} width={'100%'} opacity={0.7} />
			<Shimmer height={80} width={'100%'} opacity={0.4} />
		</Flex>
	);

	return (
		<Flex gap={tokens.spacing.x2_5}>
			<Flex gap={tokens.spacing.x3}>
				<Flex gap={tokens.spacing.x3} style={{ position: 'relative' }}>
					<Flex gap={tokens.spacing.x1}>
						<Title title={withTranslation('View.Booking.ScreenTimeSlot.Title')} titleSize='small' />
						<Text variant='labelSemiBold'>
							{localityType === LocalityType.Digital
								? withTranslation('View.Booking.ScreenTimeSlot.DigitalVideoAppointment')
								: screenTimeSlot.location?.Name}
						</Text>
					</Flex>

					<Form.Root
						onSubmit={() => {
							/* No op */
						}}
						options={{}}
						onInputChange={handleFilterChange}
					>
						<TimeSlotFilters />
					</Form.Root>
				</Flex>

				<WeeklyCalender
					date={screenTimeSlot.currentDate ?? undefined}
					fromDate={screenTimeSlot.dateSpan.from ?? undefined}
					toDate={screenTimeSlot.dateSpan.to ?? undefined}
					notifications={screenTimeSlot.availability.dates}
					onDayClick={(date) => {
						screenTimeSlot.setCurrentDate(date);
					}}
					onDateChange={(date) => {
						screenTimeSlot.setCurrentDate(date);
					}}
				/>
			</Flex>
			<Flex gap={tokens.spacing.x1} ref={containerRef} tabIndex={-1} style={{ outline: 'none' }}>
				{screenTimeSlot.currentDate ? (
					<Text variant='labelSmallRegular'>
						{capitalizeFirstLetter(
							screenTimeSlot.currentDate.format(
								screenTimeSlot.dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_TIME),
							),
						)}
					</Text>
				) : null}

				{screenTimeSlot.timeSlots.status !== 'accessible' || !screenTimeSlot.timeSlots.value ? (
					timeSlotsLoading
				) : (
					<Flex gap={tokens.spacing.x2} style={{ outline: 'none' }}>
						{screenTimeSlot.timeSlots.value.length > 0 ? (
							<Flex gap={tokens.spacing.x1}>
								<ol
									aria-label={withTranslation('View.Booking.ScreenTimeSlot.TimeSlotList.AriaLabel', {
										date: screenTimeSlot.currentDate?.format('LL') ?? '',
									})}
									style={{ display: 'contents' }}
								>
									{screenTimeSlot.timeSlots.value.map((timeSlot) => {
										const startDate = screenTimeSlot.dateService.newDate(timeSlot.Start);
										const ariaLabel = `${I18nAndL10n.translate('View.Booking.ScreenTimeSlot.TimeSlot.AriaLabel', {
											appointmentType: '',
											startTime: startDate.format('HH:mm'),
											endTime: startDate.add(timeSlot.Duration, 'minutes').format('HH:mm'),
											hcpName: timeSlot.Resource?.Name ?? '',
											hcpTitle: timeSlot.Resource?.Title ?? '',
										})} ${timeSlot.Price ? `${I18nAndL10n.translate('Common.Label.Cost')}: ${timeSlot.Price.Formatted}` : ''}`;

										return (
											<li key={timeSlot.Id} style={{ display: 'contents' }}>
												<ActionCard.Root
													onClick={handlePreviewTimeSlotClick(timeSlot.Id ?? '')}
													aria-label={ariaLabel}
													trailingIcon={null}
												>
													<Flex direction='row' gap={tokens.spacing.x2} justify='space-between' grow='1'>
														<Flex gap={tokens.spacing.x0_25}>
															<ActionCard.Title>{screenTimeSlot.dateService.newDate(timeSlot.Start).format('HH:mm')}</ActionCard.Title>
															<ActionCard.Meta>
																{timeSlot.Duration} {withTranslation('Common.Word.Minutes').toLocaleLowerCase()}
															</ActionCard.Meta>
														</Flex>
														<Flex align='flex-end' gap={tokens.spacing.x1}>
															{timeSlot.Resource ? (
																<AvatarInfo
																	name={timeSlot.Resource.Name ?? undefined}
																	title={timeSlot.Resource.Title ?? undefined}
																	avatarSrc={timeSlot.Resource.ProfileImageUrl ?? undefined}
																	align='right'
																/>
															) : null}

															{timeSlot.Price?.Value ? <BadgeText variant='info'>{timeSlot.Price?.Formatted}</BadgeText> : null}
														</Flex>
													</Flex>
												</ActionCard.Root>
											</li>
										);
									})}
								</ol>
							</Flex>
						) : (
							<EmptyState>{withTranslation('View.Booking.ScreenTimeSlot.NoAvailableTimes')}</EmptyState>
						)}

						{screenTimeSlot.nextAvailableDate ? (
							<Button variant='ghost' onClick={handleGoToNextAvailableDate} icon='arrowRight' iconPosition='trailing'>
								{withTranslation('View.Booking.ScreenTimeSlot.ButtonNextAvailableTime')}
							</Button>
						) : null}
					</Flex>
				)}
			</Flex>
			{isSmallDevice ? (
				<Sheet
					label={withTranslation('View.Booking.TimeSlotSheet.Label')}
					open={timeSlotDetailsOpen}
					onOpenChange={handleTimeSlotDetailsOpenChange}
					onCloseAutoFocus={handleAutoFocusOnDetailClose}
				>
					<Flex
						grow='1'
						style={{ width: '100%', maxWidth: '660px', margin: '0 auto', padding: `${tokens.spacing.x2} ${tokens.spacing.x2} 0` }}
					>
						<TimeSlotDetails />
					</Flex>
				</Sheet>
			) : (
				<DialogModal.Root
					open={timeSlotDetailsOpen}
					onOpenChange={handleTimeSlotDetailsOpenChange}
					onOpenAutoFocus={(e) => e.preventDefault()}
					onCloseAutoFocus={handleAutoFocusOnDetailClose}
				>
					<DialogModal.Content>
						<Flex grow='1' gap={tokens.spacing.x2}>
							<DialogModal.Title>{withTranslation('View.Booking.TimeSlotSheet.Label')}</DialogModal.Title>
							<TimeSlotDetails />
						</Flex>
					</DialogModal.Content>
				</DialogModal.Root>
			)}
			{screenTimeSlot.showUnavailableTimeSlotDialog ? <TimeSlotNotAvailableDialog /> : null}
		</Flex>
	);
}

function TimeSlotNotAvailableDialog() {
	const screenTimeSlot = useScreenTimeSlot();
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenedTimeSlotNotAvailableDialog);
		},
		[analyticCtx],
	);

	function handleAbort() {
		analyticCtx.trackEvent(ViewBookingAnalyticActions.ClosedTimeSlotNotAvailableDialog);
		screenTimeSlot.setShowUnavailableTimeSlotDialog(false);
	}

	return (
		<DialogAlert
			isOpen
			header={withTranslation('View.Booking.TimeSlotUnavailableDialog.Header')}
			description={withTranslation('View.Booking.TimeSlotUnavailableDialog.Description')}
			abortText={withTranslation('Common.Button.Close')}
			onAbort={handleAbort}
		/>
	);
}

function TimeSlotFilters() {
	const viewBookingController = useBooking();
	const screenTimeSlot = useScreenTimeSlot();
	const bookingBasket = useBookingBasket();
	const formContext = useFormContext();
	const [visible, setVisible] = useState(false);
	const flowSettings = viewBookingController.getFlowSettings();
	const analytic = useAnalyticContext();
	const basket = bookingBasket.getBasket();
	const isRescheduling = !!basket.rescheduleTicket;

	function handleClearFilter() {
		analytic.trackEvent('Click clear and close filters');
		formContext?.control.reset({
			resourceId: null,
			specialityId: null,
			unitId: null,
		});
		setVisible(false);
	}

	function handleToggleFilter() {
		analytic.trackEvent('Click show filters');
		setVisible(true);
	}

	function handleFilterChange(filter: string) {
		return () => {
			analytic.trackEvent(`Changed filter: ${filter}`, formContext?.control.getValues(filter));
		};
	}

	useEffect(() => {
		formContext?.control.reset({
			resourceId: screenTimeSlot.selectedFilters.resourceId?.toString() ?? null,
			specialityId: screenTimeSlot.selectedFilters.specialityId?.toString() ?? null,
			unitId: screenTimeSlot.selectedFilters.unitId?.toString() ?? null,
		});

		setVisible(
			screenTimeSlot.selectedFilters.resourceId != null ||
				screenTimeSlot.selectedFilters.specialityId != null ||
				screenTimeSlot.selectedFilters.unitId != null,
		);
	}, [screenTimeSlot.selectedFilters, formContext?.control]);

	if (isRescheduling) {
		return null;
	}

	const filtersData =
		screenTimeSlot.filters.status === 'accessible' && screenTimeSlot.filters.value ? screenTimeSlot.filters.value : null;

	return (
		<>
			<IconButton
				icon='sliders'
				size='medium'
				text={withTranslation('View.Booking.ScreenTimeSlot.FilterToggleTooltip')}
				variant='interactive'
				onClick={handleToggleFilter}
				style={{ position: 'absolute', top: 0, right: 0, display: visible ? 'none' : 'block' }}
			/>
			{filtersData ? (
				<AnimatePresence>
					{visible ? (
						<motion.div
							style={{ overflow: 'hidden' }}
							initial={{ height: 0 }}
							animate={{ height: 'auto' }}
							transition={{ duration: 0.2, ease: 'easeInOut' }}
							exit={{ height: 0 }}
						>
							<AutoFocus>
								<Flex gap={tokens.spacing.x1} style={{ outline: 'none' }}>
									{filtersData.resources && filtersData.resources.length > 0 ? (
										<InputCombobox.Root
											label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelStaff')}
											name='resourceId'
											optional={false}
											placeholder={withTranslation('Common.Word.All')}
											onChange={handleFilterChange('resourceId')}
											readOnly={flowSettings.lockFilters}
										>
											{filtersData.resources.map((resource) => (
												<InputCombobox.Item key={resource.Id} value={resource.Id.toString()} label={resource.Name ?? ''} />
											))}
										</InputCombobox.Root>
									) : null}
									{filtersData.specialities && filtersData.specialities.length > 0 ? (
										<InputCombobox.Root
											label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelSpeciality')}
											name='specialityId'
											optional={false}
											placeholder={withTranslation('Common.Word.All')}
											onChange={handleFilterChange('specialityId')}
											readOnly={flowSettings.lockFilters}
										>
											{filtersData.specialities.map((speciality) => (
												<InputCombobox.Item key={speciality.Id} value={speciality.Id.toString()} label={speciality.Name ?? ''} />
											))}
										</InputCombobox.Root>
									) : null}
									{filtersData.units && filtersData.units.length > 0 ? (
										<InputCombobox.Root
											label={withTranslation('View.Booking.ScreenTimeSlot.FilterLabelUnit')}
											name='unitId'
											optional={false}
											placeholder={withTranslation('Common.Word.All')}
											onChange={handleFilterChange('unitId')}
											readOnly={flowSettings.lockFilters}
										>
											{filtersData.units.map((unit) => (
												<InputCombobox.Item key={unit.Id} value={unit.Id.toString()} label={unit.Name ?? ''} />
											))}
										</InputCombobox.Root>
									) : null}
									{!flowSettings.lockFilters ? (
										<Flex align='end'>
											<Button variant='ghost' onClick={handleClearFilter} icon='cross' size='xSmall'>
												{withTranslation('View.Booking.ScreenTimeSlot.FilterClearButton')}
											</Button>
										</Flex>
									) : null}
								</Flex>
							</AutoFocus>
						</motion.div>
					) : null}
				</AnimatePresence>
			) : null}
		</>
	);
}

interface ScreenTimeSlotContentProps {
	localityType: LocalityType;
}

function TimeSlotDetails(): JSX.Element {
	const screenTimeSlot = useScreenTimeSlot();
	const authService = useResolve(AuthService);
	const analyticCtx = useAnalyticContext();
	const isSignedIn = authService.user.value != null;

	function handleSelectTimeSlot(timeSlotId: string) {
		return () => {
			if (isSignedIn) {
				analyticCtx.trackEvent(ViewBookingAnalyticActions.SelectTimeSlot);
			} else {
				analyticCtx.trackEvent(ViewBookingAnalyticActions.SelectTimeSlotAndLogin);
			}
			screenTimeSlot.selectTimeSlot(timeSlotId, isSignedIn);
		};
	}

	function handleCloseSignInSheet() {
		screenTimeSlot.clearSelectTimeSlot();
	}

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenTimeSlotDetails);
		},
		[analyticCtx],
	);

	if (screenTimeSlot.timeSlotDetails.status !== 'accessible' || !screenTimeSlot.timeSlotDetails.value) {
		return <Shimmer height={200} width={'100%'} />;
	}

	const timeSlot = screenTimeSlot.timeSlotDetails.value;

	return (
		<>
			<Flex grow='1' gap={tokens.spacing.x2_5}>
				{timeSlot.price && timeSlot.price.Value ? (
					<DataList.Root>
						<DataList.Item label={withTranslation('Common.Label.Cost')}>
							<BadgeText variant='info'>{timeSlot.price.Formatted}</BadgeText>
						</DataList.Item>
					</DataList.Root>
				) : null}

				<TimeSlotInfo
					timeSlot={{
						duration: timeSlot.duration,
						physicalLocation: timeSlot.physicalLocation,
						resource: timeSlot.resource,
						start: timeSlot.start,
					}}
				/>
				{timeSlot.description ? (
					<Flex gap={tokens.spacing.x1}>
						<Text variant='labelSemiBold'>{withTranslation('View.Booking.TimeSlotInfo.Label.Description')}</Text>
						<Text variant='bodyRegular'>{timeSlot.description}</Text>
					</Flex>
				) : null}
				<Flex
					gap={tokens.spacing.x2}
					breakpointMediumAndUp={{
						direction: 'row-reverse',
						justify: 'space-between',
					}}
				>
					{isSignedIn ? (
						<Button variant='primary' onClick={handleSelectTimeSlot(timeSlot.id)}>
							{withTranslation('View.Booking.TimeSlotSheet.ButtonSelectTimeSlot')}
						</Button>
					) : (
						<Auth.SignInSheet
							returnUrl='/booking/forms'
							trigger={<Button variant='primary'>{withTranslation('View.Booking.TimeSlotSheet.ButtonLoginAndSelectTimeSlot')}</Button>}
							onOpen={handleSelectTimeSlot(timeSlot.id)}
							onClose={handleCloseSignInSheet}
						/>
					)}

					<Sheet.Close asChild>
						<Button variant='ghost' type='button'>
							{withTranslation('Common.Button.Abort')}
						</Button>
					</Sheet.Close>
				</Flex>
			</Flex>
		</>
	);
}

const EmptyState = styled.div`
	font: ${tokens.text.label.largeRegular};
	color: ${tokens.color.content.muted};
	border: 1px dashed ${tokens.color.border.default};
	border-radius: ${tokens.borderRadius.medium};
	padding: ${tokens.spacing.x4};
	text-align: center;
`;
